import React, { useState } from 'react';
import Box from '@components/Atoms/Box';
import SectionWrapper from '@components/Atoms/SectionWrapper';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import Typography from '@components/Atoms/Typography';
import ShowIf from '@components/Atoms/ShowIf';
import { HighlightedScoreProvider } from '@components/Component/LearnosityExams/contexts/HighlightedScore/HighlightedScoreContext';
import { HideEmptyColumnsProvider } from '@components/Component/LearnosityExams/contexts/HideEmptyColumns/HideEmptyColumnsContext';
import BaseDistrictReport from '@components/Component/LearnosityExams/Components/Reports/District/BaseDistrictReport';
import DistrictExportCsvButton from '@components/Component/LearnosityExams/Components/Reports/District/DistrictExportCsvButton';
import { getReportConfig } from '@components/Component/LearnosityExams/Components/Reports/District/reportConfig';
import SubjectSelector from './SubjectSelector';
import ExamSelector from './ExamSelector';
import { useUserDistricts, SelectedSubject, SelectedExam, Curriculum } from './hooks';
import CurriculumSelector from './CurriculumSelector';

const AdminDistrictReportsWrapper: React.FC = () => {
  const [selectedSubject, setSelectedSubject] = useState<SelectedSubject | null>(null);
  const [selectedExam, setSelectedExam] = useState<SelectedExam | null>(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState<Curriculum | null>(null);
  const { districts, isLoading } = useUserDistricts();
  const districtId = districts?.length > 0 ? districts[0].id : undefined;

  const handleSubjectSelect = (subject: SelectedSubject | null) => {
    setSelectedSubject(subject);
  };

  const handleExamSelect = (exam: SelectedExam | null) => {
    setSelectedExam(exam);
    setSelectedSubject(null);
  };

  const handleCurriculumSelect = (curriculum: Curriculum | null) => {
    setSelectedCurriculum(curriculum);
    setSelectedSubject(null);
    setSelectedExam(null);
  };

  // Create the data object that district reports expect
  const reportData = selectedSubject
    ? {
        id: selectedSubject.id.toString(),
        name: selectedSubject.name,
        examId: selectedExam?.examId,
      }
    : undefined;

  return (
    <HighlightedScoreProvider>
      <ScrollWrapper sx={{ width: '100%', height: '100%' }}>
        <Box sx={{ pb: '100px', px: 3, pt: 3 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            District Learning Outcomes Reports
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <CurriculumSelector
              onCurriculumSelect={handleCurriculumSelect}
              selectedCurriculum={selectedCurriculum}
              districtId={districtId}
            />

            <ExamSelector
              onExamSelect={handleExamSelect}
              selectedExam={selectedExam}
              districtId={districtId}
              curriculumId={selectedCurriculum?.id}
            />

            <ShowIf
              If={!isLoading && !!districtId && !!selectedCurriculum && !!selectedExam}
            >
              <SubjectSelector
                onSubjectSelect={handleSubjectSelect}
                selectedSubject={selectedSubject}
                districtId={districtId}
                curriculumId={selectedCurriculum?.id}
              />
            </ShowIf>
          </Box>

          <ShowIf If={!isLoading && !!districtId && !!selectedSubject && !!selectedExam}>
            <HideEmptyColumnsProvider>
              <SectionWrapper
                title="District Learning Outcomes Report - by Skill"
                description="Track student progress across different skills at the district level"
                uniqueTestId="admin-skill-district-report-section"
                action={
                  <DistrictExportCsvButton
                    reportType="skill"
                    districtId={districtId!}
                    examId={reportData?.examId}
                    subjectId={reportData?.id}
                  />
                }
              >
                <BaseDistrictReport data={reportData} districtId={districtId} config={getReportConfig('skill')} />
              </SectionWrapper>

              <SectionWrapper
                title="District Learning Outcomes Report - by Lesson"
                description="Track student progress across different lessons at the district level"
                uniqueTestId="admin-lesson-district-report-section"
                action={
                  <DistrictExportCsvButton
                    reportType="lesson"
                    districtId={districtId!}
                    examId={reportData?.examId}
                    subjectId={reportData?.id}
                  />
                }
              >
                <BaseDistrictReport data={reportData} districtId={districtId} config={getReportConfig('lesson')} />
              </SectionWrapper>
            </HideEmptyColumnsProvider>
          </ShowIf>

          <ShowIf If={!isLoading && !districtId}>
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'grey.100',
                borderRadius: 2,
              }}
            >
              <Typography color="text.secondary">
                No district data available. Please ensure you have district admin access.
              </Typography>
            </Box>
          </ShowIf>

          <ShowIf If={!isLoading && !!districtId && (!selectedSubject || !selectedExam)}>
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'grey.100',
                borderRadius: 2,
              }}
            >
              <Typography color="text.secondary">
                {!selectedSubject
                  ? 'Please select a curriculum and subject to view reports.'
                  : 'Please select an exam session to view reports.'}
              </Typography>
            </Box>
          </ShowIf>
        </Box>
      </ScrollWrapper>
    </HighlightedScoreProvider>
  );
};

export default AdminDistrictReportsWrapper;
